import { articleData } from '@/mocks/data';

// The canonical site origin. Resolved from the environment variable so that
// the real deployment origin is always used, with a safe fallback for local runs.
export const SITE_URL: string = (
  import.meta.env.VITE_SITE_URL || 'https://allchinabuyspread.mom'
).replace(/\/+$/, '');

// Favicon provided by the site owner.
export const FAVICON_URL: string =
  'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/49750b7389edabeda6497a215e0702e5.png';

// Home hero image used as the default social share image.
const HOME_IMAGE: string =
  'https://readdy.ai/api/search-image?query=Overhead%20flatlay%20of%20curated%20fashion%20items%20including%20sneakers%20hoodie%20sunglasses%20and%20a%20leather%20bag%20arranged%20on%20a%20soft%20sage%20green%20background%2C%20bright%20airy%20natural%20light%2C%20modern%20editorial%20product%20photography%2C%20fresh%20clean%20aesthetic%20with%20subtle%20shadows%20and%20organic%20composition&width=1920&height=1080&seq=hero-main-bg-401&orientation=landscape&nocache=true';

export interface SeoData {
  type: 'website' | 'article';
  path: string;
  title: string;
  description: string;
  keywords: string;
  canonical: string;
  image?: string;
  datePublished?: string;
  dateModified?: string;
  slug?: string;
}

interface ArticleSeoMeta {
  slug: string;
  title: string;
  description: string;
  keywords: string;
  image: string;
}

const HOME_SEO = {
  title: 'AllChinaBuy Spreadsheet - Products, Sellers & Buying Guides',
  description:
    'Explore AllChinaBuy spreadsheet resources, product finds, seller research, QC guides, shipping information, warehouse tips and practical buying guides — organized in one easy-to-use resource hub.',
  keywords:
    'AllChinaBuy Spreadsheet, AllChinaBuy review, AllChinaBuy shipping, AllChinaBuy coupon, AllChinaBuy discount code, AllChinaBuy warehouse, AllChinaBuy legit, AllChinaBuy QC',
};

const articleSeoMap: ArticleSeoMeta[] = [
  {
    slug: 'allchinabuy-spreadsheet-complete-guide',
    title: 'AllChinaBuy Spreadsheet Complete Guide: Products, Sellers & Saving Money',
    description:
      'A comprehensive guide to using AllChinaBuy Spreadsheet for product research, price comparison, and finding trusted sellers. Learn shipping costs, warehouse tips, QC guides, and coupon strategies.',
    keywords:
      'AllChinaBuy Spreadsheet, AllChinaBuy spreadsheet guide, AllChinaBuy products, AllChinaBuy sellers, AllChinaBuy buying guide',
    image:
      'https://readdy.ai/api/search-image?query=Organized%20product%20research%20spreadsheet%20on%20a%20laptop%20screen%20beside%20swatches%20of%20fashion%20fabric%20and%20a%20notepad%2C%20bright%20clean%20modern%20workspace%2C%20crisp%20daylight%2C%20editorial%20productivity%20photography&width=800&height=500&seq=article-guide-201&orientation=landscape&nocache=true',
  },
  {
    slug: 'shipping-guide',
    title: 'AllChinaBuy Shipping Cost Guide: Methods, Prices & Delivery Times',
    description:
      'Compare AllChinaBuy shipping methods, costs, and delivery times to USA, UK, Germany, France, and Denmark. Learn how to reduce international shipping costs.',
    keywords:
      'AllChinaBuy shipping cost, AllChinaBuy shipping, AllChinaBuy delivery time, AllChinaBuy international shipping',
    image:
      'https://readdy.ai/api/search-image?query=Cardboard%20shipping%20boxes%20stacked%20beside%20a%20world%20map%20and%20a%20tape%20measure%20on%20a%20clean%20desk%2C%20soft%20studio%20lighting%2C%20logistics%20editorial%20photography%2C%20muted%20blue%20tones&width=800&height=500&seq=article-shipping-202&orientation=landscape&nocache=true',
  },
  {
    slug: 'warehouse-guide',
    title: 'AllChinaBuy Warehouse & QC Guide: Storage, Inspection & Consolidation',
    description:
      'Learn how AllChinaBuy warehouse storage, quality inspection, and parcel consolidation work to protect your orders and cut shipping costs.',
    keywords:
      'AllChinaBuy warehouse, AllChinaBuy QC, AllChinaBuy quality check, AllChinaBuy consolidation',
    image:
      'https://readdy.ai/api/search-image?query=Bright%20modern%20warehouse%20interior%20with%20neatly%20organized%20shelves%20of%20parcels%20and%20a%20worker%20checking%20a%20clipboard%2C%20soft%20natural%20light%20through%20large%20windows%2C%20clean%20industrial%20editorial%20photography&width=800&height=500&seq=article-warehouse-203&orientation=landscape&nocache=true',
  },
  {
    slug: 'coupon-guide',
    title: 'AllChinaBuy Coupon & Discount Code Guide: How to Save More',
    description:
      'Find the latest AllChinaBuy coupon codes, discount strategies, and practical tips to maximize your savings on every international order.',
    keywords:
      'AllChinaBuy coupon, AllChinaBuy discount code, AllChinaBuy promo code, AllChinaBuy deals',
    image:
      'https://readdy.ai/api/search-image?query=Colorful%20discount%20coupons%20and%20a%20golden%20gift%20card%20fanned%20out%20beside%20a%20calculator%20on%20a%20warm%20walnut%20desk%2C%20soft%20lighting%2C%20savings%20concept%20editorial%20photography%2C%20rich%20warm%20tones&width=800&height=500&seq=article-coupon-204&orientation=landscape&nocache=true',
  },
  {
    slug: 'safety-guide',
    title: 'Is AllChinaBuy Safe? Complete Safety & Security Guide',
    description:
      'A complete safety guide covering AllChinaBuy account security, payment risks, seller verification, and how to protect yourself while shopping internationally.',
    keywords:
      'Is AllChinaBuy safe, AllChinaBuy safety, AllChinaBuy payment security, AllChinaBuy seller verification',
    image:
      'https://readdy.ai/api/search-image?query=Digital%20security%20concept%20with%20a%20glowing%20shield%20icon%20over%20a%20padlock%20and%20keyboard%20on%20a%20clean%20dark%20blue%20desk%2C%20soft%20glowing%20light%2C%20tech%20editorial%20photography%2C%20calm%20secure%20mood&width=800&height=500&seq=article-safety-205&orientation=landscape&nocache=true',
  },
  {
    slug: 'beginners-guide',
    title: 'AllChinaBuy Beginners Guide 2026: Step-by-Step Walkthrough',
    description:
      'A step-by-step AllChinaBuy walkthrough for first-time buyers, from account setup to placing your first international order with confidence.',
    keywords:
      'AllChinaBuy beginners guide, AllChinaBuy guide, AllChinaBuy how to buy, AllChinaBuy first order',
    image:
      'https://readdy.ai/api/search-image?query=Open%20laptop%20showing%20an%20online%20shopping%20website%20with%20a%20coffee%20cup%20and%20handwritten%20notes%20on%20a%20bright%20minimal%20desk%2C%20soft%20morning%20light%2C%20cozy%20lifestyle%20editorial%20photography&width=800&height=500&seq=article-beginners-206&orientation=landscape&nocache=true',
  },
  {
    slug: 'review-2026',
    title: 'AllChinaBuy Review 2026: Honest Pros & Cons',
    description:
      'An honest, detailed AllChinaBuy review covering shipping, warehouse, QC, product range, and overall value. Learn the pros and cons before you buy.',
    keywords:
      'AllChinaBuy review, AllChinaBuy review 2026, AllChinaBuy pros and cons, AllChinaBuy worth it',
    image:
      'https://readdy.ai/api/search-image?query=Hands%20writing%20a%20review%20checklist%20with%20star%20ratings%20beside%20a%20laptop%20and%20a%20shopping%20package%20on%20a%20clean%20white%20desk%2C%20bright%20airy%20lighting%2C%20editorial%20review%20photography&width=800&height=500&seq=article-review-207&orientation=landscape',
  },
  {
    slug: 'shipping-methods',
    title: 'AllChinaBuy Shipping Methods Explained: Standard, Express & Air Freight',
    description:
      'Standard vs express vs air freight — a clear breakdown of every AllChinaBuy shipping option, including speed, cost, and when to use each.',
    keywords:
      'AllChinaBuy shipping methods, AllChinaBuy shipping guide, AllChinaBuy express shipping, AllChinaBuy air freight',
    image:
      'https://readdy.ai/api/search-image?query=Several%20delivery%20trucks%20and%20air%20freight%20parcels%20illustrated%20on%20a%20soft%20teal%20background%2C%20clean%20flat%20vector%20style%2C%20logistics%20infographic%20aesthetic%2C%20bright%20modern%20composition&width=800&height=500&seq=article-methods-208&orientation=landscape',
  },
  {
    slug: 'legit',
    title: 'Is AllChinaBuy Legit? Complete Trust & Verification Guide',
    description:
      'Is AllChinaBuy legit? Learn what to verify before ordering, red flags to avoid, and how to shop confidently with trusted sellers.',
    keywords:
      'Is AllChinaBuy legit, AllChinaBuy legitimate, AllChinaBuy trust, AllChinaBuy scam check',
    image:
      'https://readdy.ai/api/search-image?query=Trust%20badge%20and%20verified%20checkmark%20icons%20floating%20above%20a%20smartphone%20showing%20a%20shopping%20cart%2C%20soft%20green%20gradient%20background%2C%20clean%20tech%20editorial%20photography%2C%20reassuring%20mood&width=800&height=500&seq=article-legit-209&orientation=landscape',
  },
  {
    slug: 'product-research',
    title: 'AllChinaBuy Product Research Strategy: Find Winning Products',
    description:
      'Proven AllChinaBuy product research methods to find winning products, compare prices, and avoid low-quality listings.',
    keywords:
      'AllChinaBuy product research, AllChinaBuy product finds, AllChinaBuy sellers, AllChinaBuy price comparison',
    image:
      'https://readdy.ai/api/search-image?query=Magnifying%20glass%20over%20printed%20product%20photos%20and%20price%20comparison%20notes%20spread%20on%20a%20light%20desk%2C%20bright%20clean%20lighting%2C%20research%20editorial%20photography%2C%20focused%20analytic%20mood&width=800&height=500&seq=article-research-210&orientation=landscape',
  },
  {
    slug: 'warehouse-consolidation',
    title: 'AllChinaBuy Warehouse Consolidation Guide: Cut Shipping Costs',
    description:
      'Learn how AllChinaBuy warehouse consolidation combines multiple orders into one shipment to cut per-item shipping costs.',
    keywords:
      'AllChinaBuy warehouse consolidation, AllChinaBuy combine shipping, AllChinaBuy warehouse guide',
    image:
      'https://readdy.ai/api/search-image?query=Multiple%20small%20parcels%20being%20grouped%20into%20one%20large%20shipping%20box%20with%20bubble%20wrap%20on%20a%20warehouse%20table%2C%20bright%20even%20lighting%2C%20logistics%20editorial%20photography%2C%20tidy%20organized%20scene&width=800&height=500&seq=article-consolidation-211&orientation=landscape',
  },
  {
    slug: 'qc-photos',
    title: 'AllChinaBuy QC Photos: How to Read Quality Check Images',
    description:
      'Learn what to look for in AllChinaBuy QC photos before approving shipment, including defects, sizing, and color accuracy checks.',
    keywords:
      'AllChinaBuy QC photos, AllChinaBuy quality check, AllChinaBuy inspection, AllChinaBuy QC guide',
    image:
      'https://readdy.ai/api/search-image?query=Close-up%20of%20a%20camera%20photographing%20a%20folded%20garment%20on%20a%20white%20inspection%20table%20with%20a%20measuring%20tape%2C%20bright%20studio%20lighting%2C%20quality%20control%20editorial%20photography%2C%20precise%20detail&width=800&height=500&seq=article-qc-212&orientation=landscape',
  },
  {
    slug: 'seller-verification',
    title: 'AllChinaBuy Seller Verification Guide: Find Trusted Sellers',
    description:
      'How to find trusted AllChinaBuy sellers using ratings, reviews, transaction history, and verification checks before placing an order.',
    keywords:
      'AllChinaBuy seller verification, AllChinaBuy trusted sellers, AllChinaBuy seller ratings',
    image:
      'https://readdy.ai/api/search-image?query=Hand%20pointing%20at%20a%20seller%20rating%20screen%20with%20five%20stars%20and%20profile%20cards%20on%20a%20tablet%2C%20clean%20bright%20desk%20setting%2C%20trust%20and%20review%20editorial%20photography%2C%20confident%20mood&width=800&height=500&seq=article-seller-213&orientation=landscape',
  },
  {
    slug: 'coupon-strategy',
    title: 'AllChinaBuy Coupon Strategy: Save More with Smart Timing',
    description:
      'Timing, stacking, and seasonal sales tactics to maximize your AllChinaBuy discounts and save more on every order.',
    keywords:
      'AllChinaBuy coupon strategy, AllChinaBuy discount code, AllChinaBuy savings, AllChinaBuy sales',
    image:
      'https://readdy.ai/api/search-image?query=Sale%20tags%20and%20discount%20stickers%20scattered%20around%20a%20piggy%20bank%20and%20a%20calendar%20on%20a%20pastel%20desk%2C%20soft%20cheerful%20lighting%2C%20savings%20editorial%20photography%2C%20playful%20bright%20palette&width=800&height=500&seq=article-coupon-strategy-214&orientation=landscape',
  },
  {
    slug: 'shipping-usa',
    title: 'AllChinaBuy Shipping to USA: Costs, Times & Customs',
    description:
      'Everything US buyers need to know about AllChinaBuy shipping costs, delivery times, and customs when ordering to the United States.',
    keywords:
      'AllChinaBuy shipping to USA, AllChinaBuy USA shipping, AllChinaBuy customs, AllChinaBuy delivery time',
    image:
      'https://readdy.ai/api/search-image?query=Package%20with%20a%20shipping%20label%20and%20a%20small%20American%20flag%20pin%20on%20a%20clean%20map%20of%20the%20USA%2C%20bright%20natural%20lighting%2C%20delivery%20editorial%20photography%2C%20patriotic%20neutral%20palette&width=800&height=500&seq=article-usa-215&orientation=landscape',
  },
  {
    slug: 'clothing-guide',
    title: 'AllChinaBuy Clothing Guide: Shoes, Hoodies, Jackets & More',
    description:
      'A complete AllChinaBuy clothing guide covering shoes, hoodies, jackets, and more — how to buy apparel confidently from international sellers.',
    keywords:
      'AllChinaBuy clothing, AllChinaBuy shoes, AllChinaBuy hoodies, AllChinaBuy jackets, AllChinaBuy apparel',
    image:
      'https://readdy.ai/api/search-image?query=A%20neatly%20arranged%20wardrobe%20rack%20with%20shoes%20hoodies%20and%20jackets%20in%20coordinated%20neutral%20tones%2C%20soft%20warm%20lighting%2C%20fashion%20editorial%20photography%2C%20clean%20boutique%20aesthetic&width=800&height=500&seq=article-clothing-216&orientation=landscape',
  },
];

export function resolveSeo(pathname: string, lang: string): SeoData | null {
  const path = pathname === '/' ? '/' : pathname.replace(/\/+$/, '');

  if (path === '/') {
    return {
      type: 'website',
      path: '/',
      title: HOME_SEO.title,
      description: HOME_SEO.description,
      keywords: HOME_SEO.keywords,
      canonical: `${SITE_URL}/`,
      image: HOME_IMAGE,
    };
  }

  const articleMatch = path.match(/^\/article\/([^/]+)$/);
  if (articleMatch) {
    const slug = articleMatch[1];
    const meta = articleSeoMap.find((a) => a.slug === slug);
    if (!meta) {
      return null;
    }

    let title = meta.title;
    let description = meta.description;

    if (slug === 'allchinabuy-spreadsheet-complete-guide') {
      const key = (['en', 'fr', 'de', 'da'].includes(lang) ? lang : 'en') as
        | 'en'
        | 'fr'
        | 'de'
        | 'da';
      title = articleData.title[key] || articleData.title.en;
      description = articleData.metaDesc[key] || articleData.metaDesc.en;
    }

    return {
      type: 'article',
      path: `/article/${slug}`,
      title,
      description,
      keywords: meta.keywords,
      canonical: `${SITE_URL}/article/${slug}`,
      image: meta.image,
      datePublished: articleData.publishedDate,
      dateModified: articleData.publishedDate,
      slug,
    };
  }

  return null;
}